import { type RouteObject, redirect } from 'react-router-dom';
import { Root } from './Root';
import { RootFallback } from './RootFallback';
import { authenticate, authorize } from './middleware/auth.middleware';
import { ROLES } from './redux/api/user.api';
import { ErrorRoot } from './ErrorRoot';
import { store } from './redux/store';
import { projectTypesApi } from './redux/api/project-types.api';

async function hasProjectType(projectTypeId?: string): Promise<boolean> {
  if (!projectTypeId) return false;

  const request = store.dispatch(
    projectTypesApi.endpoints.getProjectTypeByID.initiate(projectTypeId, {
      forceRefetch: true
    })
  );

  try {
    const result = await request;
    if ('error' in result) return false;
    return Boolean(result.data?.data);
  } finally {
    request.unsubscribe();
  }
}

export const router: RouteObject[] = [
  {
    path: '/',
    Component: Root,
    HydrateFallback: RootFallback,
    ErrorBoundary: ErrorRoot,
    children: [
      {
        path: 'service-unavailable',
        handle: { pageTitle: 'Service Unavailable' },
        lazy: () => import('./views/ServiceUnavailable/ServiceUnavailableRoute')
      },
      {
        path: '',
        middleware: [authenticate()],
        children: [
          {
            index: true,
            handle: { pageTitle: 'Rules Engine (Admin)' },
            loader: () => redirect('/rules')
          },
          {
            path: 'rules',
            handle: { pageTitle: 'Rules Engine (Admin)' },
            middleware: [authorize([ROLES.USER, ROLES.ADMIN])],
            lazy: () => import('./views/RulesGrid/RulesGridPage')
          }
        ]
      },
      {
        path: '',
        middleware: [authenticate()],
        children: [
          {
            path: 'rules/new',
            handle: { pageTitle: 'Rules Engine (Admin)' },
            middleware: [authorize([ROLES.ADMIN])],
            lazy: () => import('./views/RulesForm/RulesFormPage')
          },
          {
            path: 'rules/:projectTypeID',
            handle: { pageTitle: 'Rules Engine (Admin)' },
            middleware: [authorize([ROLES.ADMIN])],
            loader: async ({ params }: { params: Record<string, string | undefined> }) => {
              const exists = await hasProjectType(params.projectTypeID);
              if (!exists) {
                return redirect('/service-unavailable?code=404');
              }

              return null;
            },
            lazy: () => import('./views/RulesForm/RulesFormPage')
          }
        ]
      },
      {
        path: '*',
        loader: () => redirect('/service-unavailable?code=404')
      }
    ]
  }
];
